import { Avatar, Badge, Collapse, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronRight, IconPhoto, IconStrikethrough, IconTerminal } from '@tabler/icons-react';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import ErrorDetails, { checkIsErrorAutoHealed } from '~/components/history/ErrorDetails';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import OperationInner from '~/components/test/steps/step/operation/OperationInner';
import type { AiChange } from '~/types';

export type OperationError = {
  stepId: string;
  stepIndex: number;
  stepDescription: string;
  operationId: string;
  operationDescription: string;
  error: string;
};

export type OperationLog = {
  stepId: string;
  operationId?: string;
  log: string;
};

export default function ExecutedOperationView({
  step,
  operation,
  operationError,
  operationLogs,
  aiChanges = [],
  onScreenshotClick,
  selectedOperationId,
  showError = false,
  errorScreenshotUrl,
  isErroredOperation,
  aiActionLogDisplay,
}: {
  step: SelectedStep;
  operation?: SelectedOperation;
  operationError?: OperationError;
  operationLogs?: OperationLog[];
  aiChanges?: AiChange[];
  onScreenshotClick?: () => void;
  selectedOperationId?: string;
  showError?: boolean;
  errorScreenshotUrl?: string;
  isErroredOperation?: boolean;
  /** Optional AI action log display to render before error details */
  aiActionLogDisplay?: ReactNode;
}) {
  const screenshotToDisplay =
    isErroredOperation ? errorScreenshotUrl : (operation?.actionScreenshot ?? operation?.afterScreenshot);
  const hasScreenshot = !!screenshotToDisplay;
  const isErroredWithoutScreenshot = isErroredOperation && !errorScreenshotUrl;

  const isAutoHealed = operationError ? checkIsErrorAutoHealed({ error: operationError, aiChanges }) : false;

  return operation ?
      <div className="flex flex-col">
        <div
          className={clsx('flex h-full flex-grow items-center rounded-lg', {
            'opacity-60': operation.skip,
            'border-2 border-blue-500': operation.id === selectedOperationId,
          })}
        >
          <div className="flex w-full items-center">
            {operation.pageAlias !== 'page' && (
              <Badge
                size="xs"
                className="mx-1 min-w-fit lowercase"
                color="gray"
              >
                {operation.pageAlias}
              </Badge>
            )}
            {!!operation.skip && (
              <div className="mx-2">
                <Tooltip label="Operation was skipped">
                  <Avatar
                    size="sm"
                    radius="xl"
                    className="mr-1"
                  >
                    <IconStrikethrough size={16} />
                  </Avatar>
                </Tooltip>
              </div>
            )}

            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <OperationInner
                operation={operation}
                step={step}
                previewCursorAllowed={false}
                stepIdx={step.stepOrder}
                // This step.stepOrder may want to be "idx" from Step.tsx, updated if so
                testContentId={step.testContentId || ''}
                readonly
                disabled
                editing={false}
                setEditing={() => void 0}
                disableMenu
                disableSelect
              />
              {!!operation.opensPageAlias && (
                <div className="flex gap-1 px-1">
                  <p className="text-xs opacity-50">opens </p>
                  <Badge
                    size="xs"
                    className="mx-1 min-w-fit lowercase"
                    color="gray"
                  >
                    {operation.opensPageAlias}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-1 items-center justify-end gap-2 px-2">
            {hasScreenshot ?
              <Tooltip label="View screenshot">
                <IconPhoto
                  size={16}
                  className="shrink-0 cursor-pointer text-gray-600 transition-colors hover:text-blue-600 dark:text-stone-300 dark:hover:text-blue-400"
                  onClick={onScreenshotClick}
                />
              </Tooltip>
            : null}
            {isErroredWithoutScreenshot ?
              <Tooltip label="Screenshot not available">
                <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                  <IconPhoto
                    size={14}
                    className="opacity-40"
                  />
                </div>
              </Tooltip>
            : null}
          </div>
        </div>
        {/* AI action logs - shown before error details */}
        {aiActionLogDisplay}

        {/* Inline error details - always shown when there's an error */}
        {operationError != null && showError ?
          <ErrorDetails
            error={operationError}
            isAutoHealed={isAutoHealed}
          />
        : null}

        {/* Collapsible logs - shown below error details */}
        {operationLogs != null && operationLogs.length > 0 && <CollapsibleLogs logs={operationLogs} />}
      </div>
    : <div className="h-full rounded-lg bg-zinc-100/50 dark:bg-zinc-700/30" />;
}

/**
 * Collapsible logs component - collapsed by default, click to expand
 */
function CollapsibleLogs({ logs }: { logs: OperationLog[] }) {
  const [opened, { toggle }] = useDisclosure(false);

  return (
    <div className="ml-4 mt-2">
      <UnstyledButton
        onClick={toggle}
        className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-medium text-blue-700 transition-colors hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:bg-blue-900/40"
      >
        <IconTerminal
          size={14}
          className="flex-shrink-0"
        />
        <span>
          {logs.length} log{logs.length > 1 ? 's' : ''}
        </span>
        {opened ?
          <IconChevronDown
            size={12}
            className="flex-shrink-0 opacity-60"
          />
        : <IconChevronRight
            size={12}
            className="flex-shrink-0 opacity-60"
          />
        }
      </UnstyledButton>
      <Collapse in={opened}>
        <div className="mt-2 space-y-1.5">
          {logs.map((log, index) => (
            <div
              key={index}
              className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-700 dark:bg-slate-800/50"
            >
              <Text
                size="xs"
                className="whitespace-pre-wrap font-mono text-slate-700 dark:text-slate-300"
              >
                {log.log}
              </Text>
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
}
